import React, { useEffect, useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Tooltip from '@amzn/meridian/tooltip';
import AssociateAvatar from '../../components/AssociateAvatar';
import { getAssociateDisplayLabel, getTrainedPathsSummary } from '../../utils';
import {
  AssignmentAssociate,
  DecisionReasonCode,
  OverrideDecision,
  ProcessPath,
  USER_OVERRIDE_REASON_OPTIONS,
  Workstation,
  isEmptySlot,
} from '../models';

/**
 * The destination an associate is being assigned to, resolved from the
 * cascading Assign to → Line → Station selection. `slotIndex` is the index of
 * the chosen empty slot within the workstation's `assignedAssociates`, so the
 * parent can fill exactly that planned position.
 */
export interface AssignSelection {
  fclmFunctionId: string;
  workstationId: string;
  slotIndex: number;
  /**
   * When true, the destination is an empty slot in the function's
   * `nonWorkstationAssociates` list (a no-lane pool), not a workstation slot.
   * `workstationId` is then empty and `slotIndex` indexes
   * `nonWorkstationAssociates`. Set only by the pool's inline typeahead
   * (preset mode); the cascade only ever produces workstation selections.
   */
  nonWorkstation?: boolean;
}

export interface AssignAssociateModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** The unassigned associate being assigned (chosen from the sidebar). */
  associate: AssignmentAssociate | null;
  /** All process paths for the active half — the source of the cascade. */
  processPaths: ProcessPath[];
  /**
   * When set, the destination is already known (the planner picked an
   * associate from a line's inline "Add associate" typeahead), so the modal
   * skips the Assign to → Line → Station cascade and only captures the required
   * reason. `onSubmit` then returns this exact selection. When omitted, the
   * modal shows the full cascade (sidebar Assign / Move flows).
   */
  presetSelection?: AssignSelection;
  /**
   * A human-readable label for the preset destination (e.g. the station's
   * workstation id), shown in place of the cascade. Only used in preset mode.
   */
  presetDestinationLabel?: string;
  /** Fires when the planner cancels or dismisses the modal. */
  onClose: () => void;
  /**
   * Fires when the planner confirms. Receives the resolved destination and the
   * captured `USER_OVERRIDE` decision (reason code + optional free text).
   */
  onSubmit: (selection: AssignSelection, override: OverrideDecision) => void;
}

/** Count the empty (planned-but-unfilled) slots in a workstation. */
const countEmptySlots = (workstation: Workstation): number =>
  workstation.assignedAssociates.filter(isEmptySlot).length;

/**
 * Assign popup for the redesigned Station Assignment screen (sidebar
 * "Assign" flow). Mirrors the `sample.html` prototype: a cascading
 * **Assign to** (process path) → **Line** (workstations with empty slots,
 * showing the empty count) → **Station** (a specific empty slot) selection,
 * followed by a required **reason** dropdown and optional free-text context —
 * so every assignment is captured as a `USER_OVERRIDE` decision.
 *
 * Data derivation (per requirements):
 *  - **Assign to** options: every process path that has at least one
 *    workstation with an empty slot.
 *  - **Line** options: the chosen path's workstations that have ≥1 empty slot,
 *    each labelled with its remaining empty count.
 *  - **Station** options: the empty slots within the chosen line, labelled by
 *    their per-station id (`assignedWorkstationId`).
 *
 * All controls are Meridian (Modal/Select/Textarea/Button/Row/Column/Text)
 * plus the shared AssociateAvatar. The cascade and reason state reset whenever
 * the modal closes or the associate changes.
 */
const AssignAssociateModal: React.FC<AssignAssociateModalProps> = ({
  open,
  associate,
  processPaths,
  presetSelection,
  presetDestinationLabel,
  onClose,
  onSubmit,
}) => {
  const isPreset = presetSelection != null;
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | undefined>(undefined);
  const [selectedWorkstationId, setSelectedWorkstationId] = useState<string | undefined>(undefined);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | undefined>(undefined);
  const [reasonCode, setReasonCode] = useState<DecisionReasonCode | undefined>(undefined);
  const [decisionReason, setDecisionReason] = useState<string>('');

  // Reset the whole cascade whenever the modal closes so a reopen starts clean.
  useEffect(() => {
    if (!open) {
      setSelectedFunctionId(undefined);
      setSelectedWorkstationId(undefined);
      setSelectedSlotIndex(undefined);
      setReasonCode(undefined);
      setDecisionReason('');
    }
  }, [open]);

  // Assign-to options: paths that have somewhere to place the associate —
  // either a workstation with an empty slot (line / DEPARTMENT paths) or an
  // empty slot in `nonWorkstationAssociates` (no-lane INDIRECT pool paths).
  const assignablePaths = useMemo(
    () =>
      processPaths.filter(
        (path) =>
          path.workstations.some((ws) => countEmptySlots(ws) > 0) ||
          (path.nonWorkstationAssociates ?? []).some(isEmptySlot),
      ),
    [processPaths],
  );

  const selectedPath = useMemo(
    () => assignablePaths.find((p) => p.fclmFunctionId === selectedFunctionId),
    [assignablePaths, selectedFunctionId],
  );

  // A selected path with no workstations is a non-workstation (pool) function:
  // the destination is the function's `nonWorkstationAssociates` list, so there
  // is no Line / Station cascade — the associate fills the first empty pool
  // slot and only a reason is required.
  const isNonWorkstationPath = selectedPath != null && selectedPath.workstations.length === 0;

  const firstEmptyNonWorkstationIndex = useMemo(
    () => selectedPath?.nonWorkstationAssociates?.findIndex(isEmptySlot) ?? -1,
    [selectedPath],
  );

  // Line options: workstations in the selected path that still have empties.
  const lineOptions = useMemo(
    () => (selectedPath ? selectedPath.workstations.filter((ws) => countEmptySlots(ws) > 0) : []),
    [selectedPath],
  );

  const selectedWorkstation = useMemo(
    () => lineOptions.find((ws) => ws.workstationId === selectedWorkstationId),
    [lineOptions, selectedWorkstationId],
  );

  // Station options: the empty slots within the selected line, carrying their
  // original slot index so the parent fills the exact planned position.
  const stationOptions = useMemo(() => {
    if (!selectedWorkstation) return [];
    return selectedWorkstation.assignedAssociates
      .map((slot, index) => ({ slot, index }))
      .filter(({ slot }) => isEmptySlot(slot));
  }, [selectedWorkstation]);

  const handleFunctionChange = (functionId: string) => {
    setSelectedFunctionId(functionId);
    // Downstream selections are no longer valid once the path changes.
    setSelectedWorkstationId(undefined);
    setSelectedSlotIndex(undefined);
  };

  const handleWorkstationChange = (workstationId: string) => {
    setSelectedWorkstationId(workstationId);
    setSelectedSlotIndex(undefined);
  };

  // In preset mode the destination is fixed, so only a reason is required. In
  // cascade mode the full destination must be resolved too: a non-workstation
  // path needs just the path (its first empty pool slot is the target); a
  // workstation path needs the full path/line/station selection.
  const cascadeComplete = selectedFunctionId
    ? isNonWorkstationPath
      ? true
      : !!selectedWorkstationId && selectedSlotIndex !== undefined
    : false;
  const submitDisabled = isPreset ? !reasonCode : !cascadeComplete || !reasonCode;

  const handleSubmit = () => {
    // Guard mirrors `submitDisabled`; via the UI this never fires incomplete.
    /* istanbul ignore if -- defensive: Submit is disabled until the destination + reason are complete */
    if (submitDisabled || !reasonCode) return;
    let selection: AssignSelection;
    if (isPreset) {
      selection = presetSelection as AssignSelection;
    } else if (isNonWorkstationPath) {
      selection = {
        fclmFunctionId: selectedFunctionId as string,
        workstationId: '',
        slotIndex: firstEmptyNonWorkstationIndex,
        nonWorkstation: true,
      };
    } else {
      selection = {
        fclmFunctionId: selectedFunctionId as string,
        workstationId: selectedWorkstationId as string,
        slotIndex: selectedSlotIndex as number,
      };
    }
    onSubmit(selection, {
      reasonCode,
      decisionReason: decisionReason.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign associate"
      scrollContainer="viewport"
      width="440px"
    >
      <Box data-testid="assign-associate-modal">
        {associate && (
          <Row
            alignmentVertical="center"
            spacing="300"
            spacingInset="none none 400 none"
            data-testid="assign-associate-info"
          >
            <AssociateAvatar
              associateName={associate.associateName}
              associateAlias={associate.associateAlias}
            />
            <Column spacing="none">
              <Text type="h300">
                {getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
              </Text>
              {(() => {
                // Defensive: the facade may omit `trainedPaths` entirely — show
                // nothing when there is no list. When there are many paths, show
                // a truncated summary with the full list available on hover.
                const trained = getTrainedPathsSummary(associate.trainedPaths);
                if (!trained) {
                  return null;
                }
                if (!trained.truncated) {
                  return (
                    <Text type="b100" color="secondary" data-testid="assign-trained-paths">
                      Trained: {trained.summary}
                    </Text>
                  );
                }
                return (
                  <Tooltip position="bottom" title={trained.full}>
                    <Text
                      type="b100"
                      color="secondary"
                      tag="span"
                      data-testid="assign-trained-paths"
                    >
                      Trained: {trained.summary}
                    </Text>
                  </Tooltip>
                );
              })()}
            </Column>
          </Row>
        )}

        <Column spacing="300">
          {isPreset ? (
            <Column spacing="100" data-testid="assign-preset-destination">
              <Text type="b100" color="secondary">
                {(presetSelection as AssignSelection).nonWorkstation ? 'Destination' : 'Station'}
              </Text>
              <Text type="b300">
                {presetDestinationLabel ?? (presetSelection as AssignSelection).workstationId}
              </Text>
            </Column>
          ) : (
            <>
              <Column spacing="100">
                <Text type="b100" color="secondary">
                  Assign to
                </Text>
                <Select
                  value={selectedFunctionId}
                  onChange={handleFunctionChange}
                  placeholder="Select path..."
                  width="100%"
                  size="small"
                  data-testid="assign-function-select"
                >
                  {assignablePaths.map((path) => (
                    <SelectOption
                      key={path.fclmFunctionId}
                      value={path.fclmFunctionId}
                      label={path.processPathName}
                    />
                  ))}
                </Select>
              </Column>

              {isNonWorkstationPath && (
                <Column spacing="100" data-testid="assign-nonworkstation-destination">
                  <Text type="b100" color="secondary">
                    Destination
                  </Text>
                  <Text type="b300">
                    {(selectedPath?.processPathMappingName ?? selectedPath?.processPathName) as string}{' '}
                    pool
                  </Text>
                </Column>
              )}

              {selectedPath && !isNonWorkstationPath && (
                <Column spacing="100">
                  <Text type="b100" color="secondary">
                    Line
                  </Text>
                  <Select
                    value={selectedWorkstationId}
                    onChange={handleWorkstationChange}
                    placeholder="Select line..."
                    width="100%"
                    size="small"
                    data-testid="assign-line-select"
                  >
                    {lineOptions.map((ws) => (
                      <SelectOption
                        key={ws.workstationId}
                        value={ws.workstationId}
                        label={`${ws.workstationName} (${countEmptySlots(ws)} empty)`}
                      />
                    ))}
                  </Select>
                </Column>
              )}

              {selectedWorkstation && (
                <Column spacing="100">
                  <Text type="b100" color="secondary">
                    Station
                  </Text>
                  <Select
                    value={selectedSlotIndex}
                    onChange={setSelectedSlotIndex}
                    placeholder="Select station..."
                    width="100%"
                    size="small"
                    data-testid="assign-station-select"
                  >
                    {stationOptions.map(({ slot, index }) => (
                      <SelectOption
                        key={index}
                        value={index}
                        label={slot.assignedWorkstationId ?? `Station ${index + 1}`}
                      />
                    ))}
                  </Select>
                </Column>
              )}
            </>
          )}

          <Column spacing="100">
            <Text type="b100" color="secondary">
              Reason (required)
            </Text>
            <Select
              value={reasonCode}
              onChange={setReasonCode}
              placeholder="Select reason..."
              width="100%"
              size="small"
              data-testid="assign-reason-select"
            >
              {USER_OVERRIDE_REASON_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </Column>

          <Textarea
            value={decisionReason}
            onChange={setDecisionReason}
            placeholder="Additional context (optional)"
            rows={2}
            size="small"
            data-testid="assign-context-textarea"
          />
        </Column>
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button
            type="secondary"
            size="small"
            onClick={onClose}
            data-testid="assign-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={submitDisabled}
            onClick={handleSubmit}
            data-testid="assign-submit-button"
          >
            Assign
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default AssignAssociateModal;
